import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { alertActions } from '../store/alert';
import { projectActions } from '../store/project';
import { bidActions } from '../store/bid';
import {
  Box,
  Drawer,
  Typography,
  IconButton,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Avatar,
  Button,
  Chip,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Logout as LogoutIcon,
  FolderOpen as FolderOpenIcon,
  ApartmentOutlined,
  RequestQuoteOutlined,
} from '@mui/icons-material';
import { AuthContext } from './context/auth-context';
import { getNavGroups, navItemPath } from './navigation/navConfig';
import { useWorkspace } from '../Hooks/useWorkspace';
import { canAccessBids } from '../utils/bidsAccess';
import BidSelectPanel from './bids/BidSelectPanel';
import ACCAPI from '../services/api/integrations/acc-api';
import Logger from '../utils/logger';
import packageJson from '../../package.json';
import ProjectSelectionDrawer from './projects/ProjectSelectionDrawer';
import { SIDENAV_WIDTH_VAR, SidebarContextBlock, WorkspaceSwitch } from './ui';

// Constants - matching gray-900 from Tailwind (#111827)
const DRAWER_WIDTH_EXPANDED = 256; // w-64 = 256px
// Wide enough to show a full project number (e.g. "2551002X") in the collapsed
// project chip, while staying as compact as possible.
const DRAWER_WIDTH_COLLAPSED = 96;
const STORAGE_KEY = 'sideNavCollapsed';
const SIDEBAR_BG = '#111827'; // gray-900

// Routes where the global project block is hidden: the timecard flow selects a
// project per time item, and the Projects/Employees/Workflow Template
// admin tabs are project-agnostic. Hiding is render-level only — it never clears state.
const PROJECT_HIDDEN_ROUTES = [
  '/timecards',
  '/reports',
  '/projects',
  '/employees',
  '/workflow-template',
];

const WORKSPACE_OPTIONS = [
  { value: 'project', label: 'Projects', icon: <ApartmentOutlined /> },
  { value: 'bid', label: 'Bids', icon: <RequestQuoteOutlined /> },
];

// Last page visited in each workspace, so the switch returns you there.
// sessionStorage: a same-tab convenience, never required.
const lastPathKey = (workspaceType) => `workspace:lastPath:${workspaceType}`;

const readLastPath = (workspaceType) => {
  try {
    return sessionStorage.getItem(lastPathKey(workspaceType));
  } catch {
    return null;
  }
};

// Helper function to get initials from name
const getInitials = (firstName, lastName) => {
  const first = firstName ? firstName.charAt(0).toUpperCase() : '';
  const last = lastName ? lastName.charAt(0).toUpperCase() : '';
  return first + last || 'U';
};

const SideNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = useContext(AuthContext);
  const dispatch = useDispatch();
  const appVersion = packageJson.version;

  // Global project selection (from Redux). Hidden on timecard routes only —
  // the selection itself is retained in state.
  const selectedProject = useSelector((state) => state.project.selectedProject);
  const showProjectBlock = !PROJECT_HIDDEN_ROUTES.includes(location.pathname);
  const handleOpenProjectDrawer = () =>
    dispatch(projectActions.openProjectDrawer());

  // User info
  const userFirstName = auth.userFirstName || '';
  const userLastName = auth.userLastName || '';
  const fullName = `${userFirstName} ${userLastName}`.trim() || 'User';
  const initials = getInitials(userFirstName, userLastName);
  const isAutodeskAdmin =
    fullName === 'Subat Qutlan' ||
    fullName === 'Jeff Peterson' ||
    fullName === 'Tennille Tegeler';

  // Nav groups for the current workspace (project or bid), from navConfig.
  const workspace = useWorkspace();
  const navGroups = getNavGroups(workspace.type, auth);

  // Bids workspace (#295). Users without access see the sidebar exactly as before.
  const hasBidsAccess = canAccessBids(auth);
  const isBidMode = workspace.type === 'bid';
  const selectedBid = useSelector((state) => state.bid.selectedBid);
  const isBidPanelOpen = useSelector((state) => state.bid.isPanelOpen);

  useEffect(() => {
    if (!hasBidsAccess) return;
    try {
      sessionStorage.setItem(
        lastPathKey(workspace.type),
        location.pathname + location.search
      );
    } catch {
      // Ignore — the switch falls back to each workspace's default page.
    }
  }, [hasBidsAccess, workspace.type, location.pathname, location.search]);

  const handleWorkspaceChange = (next) => {
    if (next === 'bid') {
      navigate(
        readLastPath('bid') || (selectedBid ? '/bids/overview' : '/bids')
      );
    } else {
      navigate(readLastPath('project') || '/');
    }
  };

  // Initialize collapsed state from localStorage (default to collapsed)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : true; // Default to collapsed
  });

  // User menu anchor state
  const [anchorElUser, setAnchorElUser] = useState(null);

  // Persist collapsed state to localStorage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  useEffect(() => {
    const root = document.documentElement;
    const width = isCollapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED;
    root.style.setProperty(SIDENAV_WIDTH_VAR, `${width}px`);
    return () => root.style.removeProperty(SIDENAV_WIDTH_VAR);
  }, [isCollapsed]);

  const toggleCollapse = () => {
    setIsCollapsed(!isCollapsed);
  };

  const handleNavClick = (path) => {
    navigate(path);
  };

  const handleLogout = () => {
    auth.logout();
  };

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const checkAuthStatus = async () => {
    try {
      const authStatus = await ACCAPI.getAuthStatus(auth.token);

      if (authStatus.authenticated) {
        // Authentication successful
        dispatch(
          alertActions.showAlert({
            title: '✅ Authentication Successful',
            message:
              'Autodesk authentication successful! You can now sync data.',
          })
        );
      } else {
        dispatch(
          alertActions.showAlert({
            title: '❌ Authentication Failed',
            message: 'Authentication failed or incomplete. Please try again.',
          })
        );
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      dispatch(
        alertActions.showAlert({
          title: '❌ Auth Check Error',
          message: 'Error checking authentication status: ' + error.message,
        })
      );
    }
  };

  const handleAutodeskSignIn = async () => {
    try {
      // First, get the authorization URL
      const authUrl = await ACCAPI.getAuthUrl(auth.token);

      if (authUrl) {
        // Open the authorization URL in a new window
        const authWindow = window.open(
          authUrl,
          'autodesk-auth',
          'width=600,height=600,scrollbars=yes,resizable=yes'
        );

        // Listen for messages from the popup window
        const messageHandler = (event) => {
          if (event.data.type === 'AUTODESK_AUTH_SUCCESS') {
            window.removeEventListener('message', messageHandler);
            dispatch(
              alertActions.showAlert({
                title: '✅ Authentication Successful',
                message:
                  'Autodesk authentication successful! You can now sync data.',
              })
            );
          } else if (event.data.type === 'AUTODESK_AUTH_ERROR') {
            window.removeEventListener('message', messageHandler);
            dispatch(
              alertActions.showAlert({
                title: '❌ Authentication Failed',
                message: 'Authentication failed: ' + event.data.error,
              })
            );
          }
        };

        window.addEventListener('message', messageHandler);

        // Fallback: Listen for the window to close (user completed auth)
        const checkClosed = setInterval(() => {
          if (authWindow.closed) {
            clearInterval(checkClosed);
            window.removeEventListener('message', messageHandler);
            // Check if authentication was successful
            checkAuthStatus();
          }
        }, 1000);
      } else {
        dispatch(
          alertActions.showAlert({
            title: '❌ Authorization Error',
            message: 'Failed to get authorization URL',
          })
        );
      }
    } catch (error) {
      console.error('Error starting Autodesk sign in:', error);
      dispatch(
        alertActions.showAlert({
          title: '❌ Sign In Error',
          message: 'Error starting Autodesk sign in: ' + error.message,
        })
      );
    }
  };

  const handleAutodeskSync = async () => {
    try {
      // Show confirmation dialog
      const confirmed = window.confirm(
        'This will sync all Autodesk data for all active projects. This may take several minutes. Continue?'
      );

      if (!confirmed) {
        return;
      }

      dispatch(
        alertActions.showAlert({
          title: '🔄 Autodesk Sync in Progress',
          message: 'Starting Autodesk sync... Please wait.',
          isLoading: true,
        })
      );

      // Call the sync endpoint
      const syncResult = await ACCAPI.sync(auth.token);

      // Show success message with details
      const message =
        `✅ Autodesk Sync In Progress!\n\n` +
        (syncResult.message || 'Sync started successfully');

      Logger.info('ACC sync completed', { syncResult });

      dispatch(
        alertActions.showAlert({
          title: '✅ Sync In Progress',
          message: message,
          isLoading: false,
        })
      );
    } catch (error) {
      console.error('Error during Autodesk sync:', error);
      dispatch(
        alertActions.showAlert({
          title: '❌ Sync Error',
          message: 'Error during sync: ' + error.message,
          isLoading: false,
        })
      );
    } finally {
    }
  };

  const handleAutodeskStatus = async () => {
    try {
      // Show loading message
      dispatch(
        alertActions.showAlert({
          title: '🔄 Checking Status',
          message: 'Checking Autodesk status...',
        })
      );

      // Call both status endpoints
      const [syncStatus, scheduleStatus] = await Promise.all([
        ACCAPI.getSyncStatus(auth.token),
        ACCAPI.getSyncScheduleStatus(auth.token),
      ]);

      // Format the status message
      const message =
        `📊 Autodesk Sync Status\n\n` +
        `Available Services: ${syncStatus.availableServices?.join(', ') || 'N/A'}\n` +
        `Currently Running: ${syncStatus.isRunning ? 'Yes' : 'No'}\n` +
        `Last Sync: ${syncStatus.lastSync || 'Never'}\n\n` +
        `📅 Scheduled Sync:\n` +
        `Scheduled: ${scheduleStatus?.isScheduled ? 'Yes' : 'No'}\n` +
        `Next Run: ${
          scheduleStatus?.nextRun
            ? new Date(scheduleStatus.nextRun).toLocaleString()
            : 'Not scheduled'
        }\n\n` +
        `System is ready for sync operations.`;

      dispatch(
        alertActions.showAlert({
          title: '📊 Autodesk Status',
          message: message,
        })
      );
    } catch (error) {
      console.error('Error checking Autodesk status:', error);
      dispatch(
        alertActions.showAlert({
          title: '❌ Status Check Error',
          message: 'Error checking status: ' + error.message,
        })
      );
    }
  };

  const handleToggleScheduleSync = async () => {
    try {
      // First, get the current schedule status
      const scheduleStatus = await ACCAPI.getSyncScheduleStatus(auth.token);

      const isCurrentlyScheduled = scheduleStatus?.isScheduled || false;
      const actionText = isCurrentlyScheduled ? 'stop' : 'start';

      // Show confirmation dialog
      const confirmed = window.confirm(
        `This will ${actionText} scheduled sync (daily at 2 AM). Continue?`
      );

      if (!confirmed) {
        return;
      }

      // Show loading message
      dispatch(
        alertActions.showAlert({
          title: `🔄 ${
            actionText.charAt(0).toUpperCase() + actionText.slice(1)
          }ing Schedule`,
          message: `${
            actionText.charAt(0).toUpperCase() + actionText.slice(1)
          }ing scheduled sync...`,
        })
      );

      // Call the appropriate endpoint
      const result = isCurrentlyScheduled
        ? await ACCAPI.stopSyncSchedule(auth.token)
        : await ACCAPI.startSyncSchedule(auth.token);

      const message =
        `✅ Scheduled sync ${actionText}ed successfully!\n\n` +
        `Schedule: Daily at 2:00 AM\n` +
        `Status: ${result.isScheduled ? 'Active' : 'Inactive'}\n` +
        `Next Run: ${
          result.nextRun
            ? new Date(result.nextRun).toLocaleString()
            : 'Not scheduled'
        }`;

      dispatch(
        alertActions.showAlert({
          title: '✅ Schedule Updated',
          message: message,
        })
      );
    } catch (error) {
      console.error(`Error toggling scheduled sync:`, error);
      dispatch(
        alertActions.showAlert({
          title: '❌ Schedule Toggle Error',
          message: 'Error toggling scheduled sync: ' + error.message,
        })
      );
    }
  };

  const handleMenuClick = (action) => {
    handleCloseUserMenu();
    if (action === 'Logout') {
      auth.logout();
    } else if (action === 'Change Password') {
      navigate('/changepassword');
    } else if (action === 'Autodesk Sign In') {
      handleAutodeskSignIn();
    } else if (action === 'Autodesk Sync') {
      handleAutodeskSync();
    } else if (action === 'Autodesk Status') {
      handleAutodeskStatus();
    } else if (action === 'Toggle Schedule Sync') {
      handleToggleScheduleSync();
    }
  };

  const settings = isAutodeskAdmin
    ? [
        'Autodesk Sign In',
        'Autodesk Sync',
        'Autodesk Status',
        'Toggle Schedule Sync',
        'Change Password',
      ]
    : ['Change Password'];

  const isActive = (path) => {
    // All bids is active only on /bids itself, not on /bids/overview.
    if (path === '/bids') {
      return location.pathname === '/bids';
    }
    if (path === '/analytics') {
      return (
        location.pathname === '/analytics' ||
        (location.pathname.startsWith('/analytics/') &&
          !location.pathname.startsWith('/analytics/production-metrics'))
      );
    }
    return (
      location.pathname === path || location.pathname.startsWith(path + '/')
    );
  };

  const drawerWidth = isCollapsed
    ? DRAWER_WIDTH_COLLAPSED
    : DRAWER_WIDTH_EXPANDED;

  // Render a navigation item
  const renderNavItem = (item) => {
    const Icon = item.icon;
    const path = navItemPath(item, workspace.type);
    const active = isActive(path);

    const listItemButton = (
      <ListItemButton
        onClick={() => handleNavClick(path)}
        disableRipple
        sx={{
          minHeight: 48,
          justifyContent: isCollapsed ? 'center' : 'initial',
          px: 2.5,
          borderRadius: '6px',
          mx: 1,
          backgroundColor: active ? '#1f2937' : 'transparent', // gray-800
          '&:hover': {
            backgroundColor: '#1f2937', // gray-800
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 0,
            mr: isCollapsed ? 0 : 3,
            justifyContent: 'center',
            color: active ? '#ffffff' : '#9ca3af', // white or gray-400
          }}
        >
          <Icon />
        </ListItemIcon>
        {!isCollapsed && (
          <ListItemText
            primary={item.text}
            sx={{
              '& .MuiListItemText-primary': {
                color: active ? '#ffffff' : '#9ca3af', // white or gray-400
                fontWeight: 600,
                fontSize: '0.875rem',
              },
            }}
          />
        )}
      </ListItemButton>
    );

    return (
      <ListItem key={item.text} disablePadding sx={{ display: 'block' }}>
        {isCollapsed ? (
          <Tooltip title={item.text} placement="right">
            {listItemButton}
          </Tooltip>
        ) : (
          listItemButton
        )}
      </ListItem>
    );
  };

  // Bid mode (#295): the selected bid under the logo; opens the Select bid panel.
  const renderBidBlock = () => (
    <SidebarContextBlock
      headline={selectedBid?.name}
      subline={selectedBid?.bidNumber}
      tooltip={
        selectedBid
          ? `${selectedBid.name} — ${selectedBid.bidNumber}`
          : undefined
      }
      chipLabel={selectedBid?.bidNumber}
      emptyLabel="Select a bid"
      emptyIcon={<RequestQuoteOutlined />}
      open={isBidPanelOpen}
      collapsed={isCollapsed}
      onClick={() => dispatch(bidActions.openPanel())}
    />
  );

  // Global project selector block, rendered under the logo. Hidden on timecard
  // routes; collapses to a number chip / open-drawer affordance when collapsed.
  const renderProjectBlock = () => {
    if (!showProjectBlock) return null;

    if (isCollapsed) {
      return (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            px: 1,
            pb: 1.5,
          }}
        >
          {selectedProject ? (
            <Tooltip
              title={`${selectedProject.number} — ${selectedProject.name}`}
              placement="right"
              arrow
            >
              <Chip
                label={selectedProject.number}
                onClick={handleOpenProjectDrawer}
                size="small"
                sx={{
                  cursor: 'pointer',
                  maxWidth: '100%',
                  backgroundColor: '#1f2937', // gray-800
                  color: '#ffffff',
                  fontWeight: 700,
                  '& .MuiChip-label': {
                    px: 1,
                    overflow: 'visible',
                  },
                  '&:hover': { backgroundColor: '#374151' }, // gray-700
                }}
              />
            </Tooltip>
          ) : (
            <Tooltip title="Select Project" placement="right">
              <IconButton
                onClick={handleOpenProjectDrawer}
                sx={{
                  color: '#9ca3af', // gray-400
                  '&:hover': {
                    backgroundColor: '#1f2937', // gray-800
                    color: '#ffffff',
                  },
                }}
              >
                <FolderOpenIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      );
    }

    return (
      <Box sx={{ px: 2, pb: 2, textAlign: 'center' }}>
        {selectedProject && (
          <>
            <Typography
              variant="h5"
              sx={{
                color: '#ffffff',
                fontWeight: 700,
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {selectedProject.number}
            </Typography>
            <Tooltip
              title={`${selectedProject.name} — ${selectedProject.number}`}
              placement="right"
              arrow
              enterDelay={400}
            >
              <Typography
                variant="body2"
                sx={{
                  color: '#9ca3af', // gray-400
                  mt: 0.25,
                  mb: 1.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {selectedProject.name}
              </Typography>
            </Tooltip>
          </>
        )}
        <Button
          fullWidth
          variant="contained"
          onClick={handleOpenProjectDrawer}
          sx={{
            backgroundColor: '#4f46e5', // indigo-600
            color: '#ffffff',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.875rem',
            py: 1,
            borderRadius: '6px',
            '&:hover': { backgroundColor: '#4338ca' }, // indigo-700
          }}
        >
          {selectedProject ? 'Change Project' : 'Select Project'}
        </Button>
      </Box>
    );
  };

  return (
    <>
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            backgroundColor: SIDEBAR_BG,
            color: '#ffffff',
            transition: (theme) =>
              theme.transitions.create('width', {
                easing: theme.transitions.easing.sharp,
                duration: theme.transitions.duration.enteringScreen,
              }),
            overflowX: 'hidden',
            borderRight: 'none',
          },
        }}
      >
        {/* Logo/Branding Section */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            pt: 3,
            pb: 3,
            px: isCollapsed ? 1 : 6,
          }}
        >
          <img
            src="/helmetwhite1024.png"
            alt="P1 Construction"
            style={{
              height: isCollapsed ? '32px' : '56px',
              width: 'auto',
              transition: 'height 0.3s ease',
            }}
          />
        </Box>

        {/* Global Project Selector Block */}
        {isBidMode ? renderBidBlock() : renderProjectBlock()}

        {/* Collapse Toggle Button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: isCollapsed ? 'center' : 'flex-end',
            px: 1,
            pb: 1,
          }}
        >
          <IconButton
            onClick={toggleCollapse}
            sx={{
              color: '#9ca3af', // gray-400
              '&:hover': {
                backgroundColor: '#1f2937', // gray-800
                color: '#ffffff',
              },
            }}
          >
            {isCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />

        {/* Navigation Items — one List per nav group, divider between groups */}
        <Box sx={{ flexGrow: 1, py: 2 }}>
          {navGroups.map((group, index) => (
            <React.Fragment key={group.id}>
              {index > 0 && (
                <Divider
                  sx={{
                    borderColor: 'rgba(255, 255, 255, 0.08)',
                    my: 1,
                    mx: 2,
                  }}
                />
              )}
              <List>{group.items.map((item) => renderNavItem(item))}</List>
            </React.Fragment>
          ))}
        </Box>

        <Divider sx={{ borderColor: 'rgba(255, 255, 255, 0.08)' }} />

        {/* Workspace switch (#295) — only for users who can open Bids */}
        {hasBidsAccess && (
          <Box
            sx={{
              px: isCollapsed ? 1 : 2,
              pt: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: isCollapsed ? 'center' : 'stretch',
            }}
          >
            <WorkspaceSwitch
              options={WORKSPACE_OPTIONS}
              value={workspace.type}
              onChange={handleWorkspaceChange}
              collapsed={isCollapsed}
            />
          </Box>
        )}

        {/* User Profile Section */}
        <Box
          sx={{
            px: isCollapsed ? 1 : 2,
            py: 2,
          }}
        >
          {/* User Info - Clickable with dropdown */}
          <Box
            onClick={handleOpenUserMenu}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              mb: isCollapsed ? 1 : 1.5,
              justifyContent: isCollapsed ? 'center' : 'flex-start',
              cursor: 'pointer',
              padding: 1,
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: '#1f2937', // gray-800
              },
            }}
          >
            {isCollapsed ? (
              <Tooltip title={fullName} placement="right">
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    backgroundColor: '#1f2937', // gray-800
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                  }}
                >
                  {initials}
                </Avatar>
              </Tooltip>
            ) : (
              <>
                <Avatar
                  sx={{
                    width: 36,
                    height: 36,
                    backgroundColor: '#374151', // gray-700
                    color: '#ffffff',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                  }}
                >
                  {initials}
                </Avatar>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#ffffff',
                    fontWeight: 600,
                    fontSize: '0.875rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {fullName}
                </Typography>
              </>
            )}
          </Box>

          {/* User Dropdown Menu */}
          <Menu
            id="user-menu"
            anchorEl={anchorElUser}
            anchorOrigin={{
              vertical: 'center',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'center',
              horizontal: 'left',
            }}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
            sx={{
              ml: 1,
            }}
          >
            {settings.map((setting) => (
              <MenuItem key={setting} onClick={() => handleMenuClick(setting)}>
                <Typography textAlign="center">{setting}</Typography>
              </MenuItem>
            ))}
          </Menu>

          {/* Sign Out Button */}
          {!isCollapsed ? (
            <Button
              onClick={handleLogout}
              startIcon={<LogoutIcon />}
              sx={{
                width: '100%',
                justifyContent: 'center',
                backgroundColor: '#dc2626', // red-600
                color: '#ffffff',
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.875rem',
                py: 1,
                borderRadius: '6px',
                '&:hover': {
                  backgroundColor: '#b91c1c', // red-700
                },
              }}
            >
              Sign Out
            </Button>
          ) : (
            <Tooltip title="Sign Out" placement="right">
              <IconButton
                onClick={handleLogout}
                sx={{
                  color: '#ffffff',
                  backgroundColor: '#dc2626', // red-600
                  display: 'flex',
                  justifyContent: 'center',
                  width: '100%',
                  borderRadius: '6px',
                  '&:hover': {
                    backgroundColor: '#b91c1c', // red-700
                  },
                }}
              >
                <LogoutIcon />
              </IconButton>
            </Tooltip>
          )}

          <Typography
            variant="caption"
            sx={{
              mt: 1.25,
              color: 'rgba(255, 255, 255, 0.55)',
              display: 'block',
              textAlign: 'center',
              fontSize: '0.62rem',
              lineHeight: 1.2,
              whiteSpace: 'nowrap',
            }}
          >
            v{appVersion}
          </Typography>
        </Box>
      </Drawer>
      <ProjectSelectionDrawer />
      {hasBidsAccess && <BidSelectPanel />}
    </>
  );
};

export default SideNav;
